import { FC, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AppBar, Box, Paper, Stack, Toolbar, Typography } from '@mui/material'
import { useReadLocalStorage } from 'usehooks-ts'

import { IPSNameLogo } from '@/assets/icons'
import { IPS_METRO_BLUE } from '@/common/constants'
import { LocalStorageKey, removeLocalStorageValue } from '@/common/utils/localStorage'
import LoginFormOIDC from '@/modules/authentication/LoginFormOIDC'
import LoginFormPassword from '@/modules/authentication/LoginFormPassword'

const LoginForm: FC = () => {
  const { t } = useTranslation()

  const previouslyLoggedInUser = useReadLocalStorage<{
    providers?: string[]
    email: string
  }>(LocalStorageKey.USER) ?? { providers: undefined, email: '' }
  const { providers, email: previousEmail } = previouslyLoggedInUser

  useEffect(() => {
    removeLocalStorageValue(LocalStorageKey.LAST_ACTIVE_TIME)
  }, [])

  const oidcProvider = providers?.find(provider => provider.startsWith('oidc.'))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar color="inherit" position="static">
        <Toolbar>
          <Stack direction="row" alignItems="center" height="40px">
            <IPSNameLogo />
          </Stack>
        </Toolbar>
      </AppBar>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Fill whatever is left below the app bar rather than a hardcoded 100vh, which
          // would push the form off screen.
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Box sx={{ width: '100%', maxWidth: 600, m: '15px', p: '1rem' }}>
            <Paper
              className="login-form"
              data-testid="login-form"
              sx={{ py: 'calc(1em + 30px)', px: 'calc(1em + 20px)' }}
            >
              <Typography
                variant="h5"
                component="h2"
                sx={{ color: IPS_METRO_BLUE, fontWeight: 'bold', mb: 2 }}
              >
                {t('login.pageTitle', 'Login').toLocaleUpperCase()}
              </Typography>
              {oidcProvider ? (
                <LoginFormOIDC initialEmail={previousEmail} providerId={oidcProvider} />
              ) : (
                <LoginFormPassword initialEmail={previousEmail} />
              )}
              <br />
              <Trans i18nKey={'login.termsOfService'}>
                By logging in you agree to the following{' '}
                <a
                  href="https://www.populus.ai/legal/terms-of-service"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Terms of Service
                </a>
                .
              </Trans>
              <br />
              <Trans i18nKey={'login.requestDemo'}>
                {`If you don't have an account, you may want to `}
                <a href="https://www.populus.ai/contact/" rel="noopener noreferrer" target="_blank">
                  request a demo
                </a>
                .
              </Trans>
            </Paper>
          </Box>
        </Box>
      </div>
    </div>
  )
}

export default LoginForm
